import React, { createContext, useEffect, useMemo, useState } from 'react';
import { merge } from 'lodash';
import { Store } from '@watchable/store';
import { edit } from '@watchable/store-edit';
import { useStore } from '@watchable/store-react';

import { useAspenLogger } from 'hooks/useAspenLogger';
import { useAspenSocket } from 'hooks/useAspenSocket';
import { convertFilterStateToQueryStatesPayload, mergeEvents } from 'modules/events';
import { getTimeRangeBasedOnRetention } from 'modules/time';

import { ActivityState } from 'types/aspen/events';
import { AspenSocketMsg, QueryStatesPayload } from 'types/aspen/socketMessages';

export const EVENTS_HISTORY_RETENTION_DAYS = 14; // days

export type EventsStore = {
  count: number;
  states: Array<ActivityState>;
  nextPageToken: string;
};

interface EventsContext {
  eventsStore: Store<EventsStore>;
  loading: boolean;
  modalOpen: boolean;
  filterState: Omit<QueryStatesPayload, 'nextPageToken'>;
  socketError: Error | null;
  queryEvents: () => void;
  queryNextPage: () => void;
  resetStore: () => void;
  sendRequest: ReturnType<typeof useAspenSocket>['sendRequest'];
  setFilterState: React.Dispatch<React.SetStateAction<EventsContext['filterState']>>;
  setModalOpen: React.Dispatch<React.SetStateAction<EventsContext['modalOpen']>>;
}

export const EventsContext = createContext<EventsContext>({} as EventsContext);

interface EventsProviderProps {
  children: React.ReactNode;
}

const INITIAL_STORE: EventsStore = {
  count: 0,
  states: [],
  nextPageToken: '',
};

export const getInitialFilterState = (): EventsContext['filterState'] => {
  const { earliestStartTime, latestEndTime } = getTimeRangeBasedOnRetention(EVENTS_HISTORY_RETENTION_DAYS);
  return {
    startTime: earliestStartTime,
    endTime: latestEndTime,
    leaves: [],
    objectsDetected: [],
    order: 'desc',
    triggers: [],
    pageLimit: 50,
  };
};

export const INITIAL_FILTER_STATE: EventsContext['filterState'] = getInitialFilterState();

export const EventsProvider = ({ children }: EventsProviderProps) => {
  const { logAspenEvent } = useAspenLogger();

  const eventsStore = useStore<EventsStore>(INITIAL_STORE);
  const [loading, setLoading] = useState<EventsContext['loading']>(true);
  const [socketError, setSocketError] = useState<EventsContext['socketError']>(null);
  const [modalOpen, setModalOpen] = useState<EventsContext['modalOpen']>(false);

  const sessionStorageFilterState = sessionStorage.getItem('eventsFilterState');
  const initialFilterState =
    sessionStorageFilterState !== null
      ? convertFilterStateToQueryStatesPayload({
          filterState: JSON.parse(sessionStorageFilterState),
          retentionDays: EVENTS_HISTORY_RETENTION_DAYS,
        })
      : getInitialFilterState();
  const [filterState, setFilterState] = useState<EventsContext['filterState']>(initialFilterState);

  const { connection, subscribe, unsubscribe, queryStates, sendRequest } = useAspenSocket();

  useEffect(() => {
    subscribe((messageEvent: MessageEvent) => {
      const message: AspenSocketMsg = JSON.parse(messageEvent.data);

      if (message.type === 'query_states_resp') {
        if (message.payload) {
          edit(eventsStore, (updatedState) => {
            merge(updatedState, {
              count: updatedState.count + message.payload.states.length,
              states: mergeEvents({
                currentEvents: updatedState.states,
                nextEvents: message.payload.states,
              }),
              nextPageToken: message.payload.nextPageToken,
            });
          });
        }

        // Log the query event
        logAspenEvent({
          log_id: 'iot.web.event.query.response',
          payload: {
            request_id: message.id,
            success: !!message.payload && !message.error,
            ...(!message.error && { has_results: message.payload.states.length > 0 }),
            ...(message.error && { error: message.error }),
          },
        });

        setLoading(false);
      }
    });

    // Subscribe to socket errors
    connection.onerror = () => {
      if (connection.retryCount === 5) {
        setSocketError(new Error('WebSocket error.'));
        setLoading(false);
      }
    };

    return unsubscribe;
  }, [eventsStore, connection]);

  useEffect(() => {
    // Reset store when filter state changes
    queryEvents();
  }, [filterState]);

  const queryEvents: EventsContext['queryEvents'] = () => {
    // Reset the store before querying
    resetStore();

    // Query events
    setLoading(true);
    queryStates({
      payload: {
        ...filterState,
      },
    });
  };

  const queryNextPage = () => {
    setLoading(true);
    queryStates({
      payload: {
        ...filterState,
        nextPageToken: eventsStore.read().nextPageToken,
      },
    });
  };

  const resetStore = () => {
    eventsStore.write(INITIAL_STORE);
  };

  const contextValue = useMemo(
    () => ({
      eventsStore,
      loading,
      filterState,
      modalOpen,
      socketError,
      queryEvents,
      queryNextPage,
      resetStore,
      sendRequest,
      setFilterState,
      setModalOpen,
    }),
    [eventsStore, loading, filterState, socketError, queryEvents, queryNextPage, resetStore, sendRequest],
  );

  return <EventsContext.Provider value={contextValue}>{children}</EventsContext.Provider>;
};
